import { Component, ElementRef, EventEmitter, Input, Output, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { OverlayPanel } from 'primeng/overlaypanel';
import { asapScheduler, combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, map, observeOn, startWith, switchMap, take } from 'rxjs/operators';
import { DwSecurityUserService } from '@devwareapps/devware-cap';
import { ChallengeContextService, ChallengeContextData } from '../../contexts/challenge-context/challenge-context.service';
import { ChallengeDefinitionEntity } from '../../../../meta-data/app-meta-data.service';
import { FitnessReferenceDataService } from '../../services/fitness-reference-data.service';

@Component({
    selector: 'app-challenge-selector',
    templateUrl: './challenge-selector.component.html',
    styleUrls: ['./challenge-selector.component.scss']
})
export class ChallengeSelectorComponent implements OnInit, OnDestroy {
    @Input() selectionGuard: () => Observable<boolean>;
    @Input() title = '';
    @ViewChild('streakToggle') streakToggle: ElementRef<HTMLButtonElement>;
    @ViewChild('streakPanel') streakPanel: OverlayPanel;
    @Output() challengeSelected = new EventEmitter<void>();
    expanded = false;
    search = '';
    currentStreak: ChallengeDefinitionEntity;
    private definitions: ChallengeDefinitionEntity[] = [];
    get streakChoices(): ChallengeDefinitionEntity[] {
        const choices = [...this.challenges];
        if (this.currentStreak && !choices.some(c => c.ChallengeDefinitionId === this.currentStreak.ChallengeDefinitionId)) {
            choices.push(this.currentStreak);
        }
        const selected = this.definitions.find(c => c.ChallengeDefinitionId === this.selectedChallengeId);
        return [selected, this.currentStreak, ...choices].filter((c, index, rows) => c &&
            rows.findIndex(row => row?.ChallengeDefinitionId === c.ChallengeDefinitionId) === index);
    }
    get filteredStreaks(): ChallengeDefinitionEntity[] {
        const search = this.search.trim().toLocaleLowerCase();
        return this.streakChoices.filter(c => (c.Name || '').toLocaleLowerCase().includes(search));
    }
    get canChangeStreak(): boolean { return this.streakChoices.length > 1 && !this.loadError; }
    private productGraphics: { [productId: number]: number } = {};
    challengePhoto(challenge: ChallengeDefinitionEntity): number {
        return this.productGraphics[challenge.ProductId];
    }
    toggleStreaks(event: MouseEvent, target: HTMLElement): void {
        if (!this.canChangeStreak) { return; }
        this.search = '';
        this.streakPanel.toggle(event, target);
    }
    closeStreaks(): void {
        this.streakPanel?.hide();
        this.streakToggle?.nativeElement.focus();
    }
    challenges: ChallengeDefinitionEntity[] = [];
    selectedChallengeId: number = 0;

    private sub = new Subscription();
    loadError = '';

    constructor(
        private challengeContextService: ChallengeContextService,
        private referenceData: FitnessReferenceDataService,
        private security: DwSecurityUserService,
    ) {}

    ngOnInit() {
        this.sub.add(this.challengeContextService.challengeChange$.subscribe(c => {
            this.selectedChallengeId = c?.ChallengeDefinitionId || 0;
        }));
        this.loadChallenges();
    }

    ngOnDestroy() {
        if (this.sub) { this.sub.unsubscribe(); }
        this.streakPanel?.hide();
    }

    private loadChallenges() {
        // A cached response can be synchronous. Selecting the default updates parent
        // dashboard state, so publish it after the current render check.
        this.sub.add(this.security.securityContextChanged().pipe(startWith(null),
            switchMap(() => combineLatest([this.referenceData.getChallengeDefinitions(),
                this.referenceData.getBuyerChallengeHistory()]).pipe(take(1),
                    switchMap(([definitions, history]) => this.referenceData.getStreakProductMedia(
                        Array.from(new Set((definitions || []).map(c => c.ProductId).filter(id => id > 0)))
                    ).pipe(catchError(() => of([])), map(media => {
                        this.productGraphics = {};
                        media.filter(row => row.MediaId > 0).forEach(row => {
                            if (!this.productGraphics[row.ProductId]) { this.productGraphics[row.ProductId] = row.MediaId; }
                        });
                        return [definitions, history] as [ChallengeDefinitionEntity[], typeof history];
                    }))))), observeOn(asapScheduler)).subscribe(([result, history]) => {
            const registered = new Set(history.map(row => row.ChallengeDefinitionId));
            this.definitions = result || [];
            this.challenges = (result || []).filter(c => registered.has(c.ChallengeDefinitionId));
            this.currentStreak = (result || []).find(c => c.IsCurrent === true);
            this.loadError = '';
            if (!(result || []).some(c => c.ChallengeDefinitionId === this.selectedChallengeId)) {
                const active = (result || []).filter(c => c.IsCurrent === true);
                if (active.length !== 1) {
                    this.loadError = 'Exactly one active challenge must be configured.';
                    return;
                }
                this.selectChallenge(this.challenges[0] || active[0]);
            }
        }, () => this.loadError = 'Unable to load challenges. Please reload.'));
    }

    selectChallenge(challenge: ChallengeDefinitionEntity) {
        const allowed = this.selectionGuard ? this.selectionGuard() : of(true);
        this.sub.add(allowed.pipe(take(1)).subscribe(canChange => {
            if (!canChange) { return; }
            this.selectedChallengeId = challenge.ChallengeDefinitionId;
            this.challengeContextService.setChallenge({
                ChallengeDefinitionId: challenge.ChallengeDefinitionId,
                ChallengeName: challenge.Name || '',
            });
            this.challengeSelected.emit();
            if (this.title && this.expanded) { this.closeStreaks(); }
        }));
    }

    onSelectionChange(event: any) {
        const selected = this.challenges.find(c => c.ChallengeDefinitionId === +event.target.value);
        // Keep the DOM aligned with the current selection until the guard approves.
        event.target.value = this.selectedChallengeId;
        if (selected) {
            this.selectChallenge(selected);
        }
    }
}
